import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChatMessage } from './useChat';

/**
 * THE RULE: entering an RFQ always opens the chat fresh.
 *
 * However long ago the last message was — three months or three minutes — the
 * panel opens on Robbie's resting state for the step, with one quiet line back
 * to whatever was said before. Nothing is deleted; the scrollback is folded,
 * not dropped. There is deliberately no age threshold: "did this conversation
 * go stale?" turned out to be the wrong question, because the person decides
 * they are starting something new by walking into the RFQ, not by waiting.
 *
 * A VISIT is one stay inside one RFQ: it begins when the wizard's panel mounts
 * for an RFQ (arriving from the dashboard, from another RFQ, from a reload)
 * and ends when the person leaves. Moving between wizard steps is the same
 * visit — the router keeps the panel mounted and only changes its props — so a
 * thread someone unfolded on step 2 is still unfolded on step 4.
 */
const FOLD_STORAGE_PREFIX = 'robbie.chat.fold.';

/**
 * The person's choice inside one visit, stamped with the visit it belongs to.
 * The count is the fold itself: a fresh entry folds everything, "New
 * conversation" folds everything up to the cut, and an unfold is zero. The
 * stamp is what makes the next entry ignore it rather than reopen the old
 * scrollback.
 */
interface StoredFold {
  visit: string;
  foldedCount: number;
}

const storageKey = (threadKey: string) => `${FOLD_STORAGE_PREFIX}${threadKey}`;

// sessionStorage throws outright in some privacy modes, so every touch is
// guarded: a lost preference is a cosmetic regression, a thrown panel is not.
const readFold = (threadKey: string): StoredFold | null => {
  try {
    const raw = window.sessionStorage.getItem(storageKey(threadKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.foldedCount !== 'number' || typeof parsed?.visit !== 'string') return null;
    return parsed as StoredFold;
  } catch {
    return null;
  }
};

const writeFold = (threadKey: string, fold: StoredFold): void => {
  try {
    window.sessionStorage.setItem(storageKey(threadKey), JSON.stringify(fold));
  } catch {
    /* no persistence available — the choice still holds for this mount */
  }
};

let nextVisitId = 0;

/**
 * Visits whose panel has just unmounted and that a remount may still claim.
 *
 * Insurance for one case: React rebuilding the panel in place, in a single
 * commit, without the person going anywhere — a route element swapped for an
 * identical one, a re-keyed parent. There, the teardown and the next mount are
 * in the SAME commit, so a layout effect on the way in always runs before a
 * zero-delay timer set on the way out, and the visit carries over. Anything
 * with a gap in it — the wizard sitting in its loading state while the RFQ
 * refetches, and certainly a trip to the dashboard — is a new entry, and the
 * chat opens fresh. That is the rule, so the bound being tight is the point.
 */
const releasedVisits = new Map<string, { visit: string; timer: ReturnType<typeof setTimeout> }>();

const claimVisit = (threadKey: string): string => {
  const released = releasedVisits.get(threadKey);
  if (released) {
    clearTimeout(released.timer);
    releasedVisits.delete(threadKey);
    return released.visit;
  }
  nextVisitId += 1;
  return `v${nextVisitId}`;
};

const releaseVisit = (threadKey: string, visit: string): void => {
  const timer = setTimeout(() => releasedVisits.delete(threadKey), 0);
  releasedVisits.set(threadKey, { visit, timer });
};

const RELATIVE_UNITS: Array<{ limitMs: number; divisorMs: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { limitMs: 60_000, divisorMs: 1_000, unit: 'second' },
  { limitMs: 3_600_000, divisorMs: 60_000, unit: 'minute' },
  { limitMs: 86_400_000, divisorMs: 3_600_000, unit: 'hour' },
  { limitMs: 604_800_000, divisorMs: 86_400_000, unit: 'day' },
  { limitMs: 2_629_800_000, divisorMs: 604_800_000, unit: 'week' },
  { limitMs: Number.POSITIVE_INFINITY, divisorMs: 2_629_800_000, unit: 'month' },
];

/**
 * "2 hours ago", "yesterday", "just now" — `numeric: 'auto'` is what turns the
 * one-day case into "yesterday" rather than the stilted "1 day ago".
 */
export const formatRelativeTime = (iso: string, now: number = Date.now()): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'earlier';
  const elapsed = Math.max(0, now - then);
  if (elapsed < 45_000) return 'just now';
  const { divisorMs, unit } = RELATIVE_UNITS.find((u) => elapsed < u.limitMs) ?? RELATIVE_UNITS[RELATIVE_UNITS.length - 1];
  const value = Math.round(elapsed / divisorMs);
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-value, unit);
};

export interface ChatThreadView {
  /** The messages the panel should render — the folded ones are omitted, never dropped. */
  visibleMessages: ChatMessage[];
  /**
   * Nothing is visible, so the panel shows Robbie's resting state. This is
   * exactly "no visible messages" and nothing else: any other condition leaves
   * a state in which the panel renders neither a thread nor a greeting, which
   * is a blank rail.
   */
  resting: boolean;
  /** How many earlier messages are folded away behind the continue line. */
  foldedCount: number;
  /** `createdAt` of the last folded message — what the continue line dates itself from. */
  foldedUntil?: string;
  /** Reveal the folded messages in place. */
  unfold: () => void;
  /** Cut a fresh conversation: everything so far folds behind the continue line. */
  startNewConversation: () => void;
  /** Bumped by `unfold` only, so the panel can play the reveal for that one case. */
  unfoldNonce: number;
}

/**
 * Decides how much of a chat thread the panel shows. Presentation only: it
 * reads the messages `useChat` already holds and never touches the request
 * Robbie answers from — the server builds the model's history from its own
 * stored session, and the browser sends it none. Folding a thread here cannot
 * narrow what Robbie knows.
 */
export const useChatThreadView = (
  threadKey: string | undefined,
  messages: ChatMessage[],
  /** `useChat.historyLoaded` — deciding before it is true would judge an empty thread. */
  historyLoaded: boolean,
): ChatThreadView => {
  // `null` means "this visit has not chosen yet", which resolves to the rule:
  // fold everything. It is never a state the panel can render blank — see the
  // default in `foldedCount` below.
  const [fold, setFold] = useState<number | null>(null);
  const [unfoldNonce, setUnfoldNonce] = useState(0);
  const visitRef = useRef('');
  const decidedForRef = useRef<string | null>(null);

  // Layout effect, not a passive one: the visit has to be claimed inside the
  // commit that mounts the panel, or a remount cannot tell itself apart from a
  // fresh entry. See `releasedVisits`.
  useLayoutEffect(() => {
    const key = threadKey ?? '';
    visitRef.current = claimVisit(key);
    return () => releaseVisit(key, visitRef.current);
  }, [threadKey]);

  useEffect(() => {
    if (!historyLoaded) return;
    const key = threadKey ?? '';
    if (decidedForRef.current === key) return;
    decidedForRef.current = key;

    const stored = threadKey ? readFold(threadKey) : null;
    if (stored && stored.visit === visitRef.current) {
      // Same visit, already chosen — a step change, or the panel rebuilt under
      // the person without them going anywhere. Their choice stands.
      setFold(stored.foldedCount);
      return;
    }

    // A new entry into this RFQ: everything said before belongs to the last
    // visit, so it folds away and Robbie opens on his resting state.
    setFold(messages.length);
    if (threadKey) writeFold(threadKey, { visit: visitRef.current, foldedCount: messages.length });
  }, [threadKey, messages, historyLoaded]);

  const commit = useCallback(
    (foldedCount: number) => {
      setFold(foldedCount);
      if (threadKey) writeFold(threadKey, { visit: visitRef.current, foldedCount });
    },
    [threadKey],
  );

  const unfold = useCallback(() => {
    commit(0);
    setUnfoldNonce((n) => n + 1);
  }, [commit]);

  const startNewConversation = useCallback(() => commit(messages.length), [commit, messages.length]);

  // Undecided defaults to "fold everything" rather than "fold nothing": that is
  // the rule, and it also means the first paint of a visit is already the
  // resting state instead of a flash of the old scrollback.
  const foldedCount = Math.min(fold ?? messages.length, messages.length);
  const visibleMessages = useMemo(() => messages.slice(foldedCount), [messages, foldedCount]);

  return {
    visibleMessages,
    resting: visibleMessages.length === 0,
    foldedCount,
    foldedUntil: foldedCount > 0 ? messages[foldedCount - 1]?.createdAt : undefined,
    unfold,
    startNewConversation,
    unfoldNonce,
  };
};
